import React from 'react'
import PropTypes from 'prop-types'
import {
  Modal,
  Select,
  Table,
  Button,
  Tag,
  message,
  Alert,
  Spin,
} from 'antd'
import { SendOutlined } from '@ant-design/icons'
import { getRamsProjectsList, getRamsProjectSubcontractors, createRamsRun } from '../../../../HttpRequests/ramsControlCentre'

var Option = Select.Option

export default class NewRunModal extends React.PureComponent {

  static propTypes = {
    visible: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onRunCreated: PropTypes.func,
  }

  state = {
    projects: [],
    loadingProjects: false,
    selectedProjectId: null,
    subcontractors: [],
    ramsRecords: [],
    projectTitle: '',
    selectedSubcontractorIds: [],
    selectedRamsRecordId: null,
    loadingSubcontractors: false,
    submitting: false,
  }

  componentDidUpdate(prevProps) {
    if (this.props.visible && !prevProps.visible) {
      this.setState({
        selectedProjectId: null,
        subcontractors: [],
        ramsRecords: [],
        projectTitle: '',
        selectedSubcontractorIds: [],
        selectedRamsRecordId: null,
        loadingSubcontractors: false,
        submitting: false,
      })
      this.loadProjects()
    }
  }

  loadProjects = () => {
    var self = this
    this.setState({ loadingProjects: true })

    getRamsProjectsList()
      .then(function (response) {
        var data = response.data || []
        self.setState({ projects: data, loadingProjects: false })
      })
      .catch(function (error) {
        console.error('Failed to load projects:', error)
        self.setState({ loadingProjects: false })
        message.error('Failed to load projects')
      })
  }

  handleProjectChange = (projectId) => {
    var self = this
    this.setState({
      selectedProjectId: projectId,
      subcontractors: [],
      ramsRecords: [],
      projectTitle: '',
      selectedSubcontractorIds: [],
      selectedRamsRecordId: null,
      loadingSubcontractors: true,
    })

    getRamsProjectSubcontractors(projectId)
      .then(function (response) {
        var data = response.data
        var subs = data.subcontractors || []
        var records = data.rams_records || []
        var allIds = subs.map(function (s) { return s.id })

        self.setState({
          subcontractors: subs,
          ramsRecords: records,
          projectTitle: (data.project && data.project.title) || '',
          selectedSubcontractorIds: allIds,
          selectedRamsRecordId: (records.length > 0) ? records[0].id : null,
          loadingSubcontractors: false,
        })
      })
      .catch(function () {
        self.setState({ loadingSubcontractors: false })
        message.error('Failed to load project data')
      })
  }

  handleSubcontractorSelectionChange = (selectedRowKeys) => {
    this.setState({ selectedSubcontractorIds: selectedRowKeys })
  }

  handleRamsRecordChange = (recordId) => {
    this.setState({ selectedRamsRecordId: recordId })
  }

  handleSubmit = () => {
    var self = this
    var { selectedProjectId, selectedSubcontractorIds, selectedRamsRecordId } = this.state

    if (!selectedProjectId) {
      message.warning('Please select a project')
      return
    }

    if (selectedSubcontractorIds.length === 0) {
      message.warning('Please select at least one subcontractor')
      return
    }

    this.setState({ submitting: true })

    var data = {
      project_id: selectedProjectId,
      subcontractor_ids: selectedSubcontractorIds,
    }

    if (selectedRamsRecordId) {
      data.rams_record_id = selectedRamsRecordId
    }

    createRamsRun(data)
      .then(function (response) {
        self.setState({ submitting: false })
        var result = response.data
        message.success(
          'RAMS run created: ' + (result.total_sent || 0) + ' sent, ' +
          (result.emails_sent || 0) + ' emails queued'
        )
        self.props.onClose()
        if (self.props.onRunCreated) {
          self.props.onRunCreated()
        }
      })
      .catch(function (err) {
        self.setState({ submitting: false })
        var errorMsg = 'Failed to create RAMS run'
        if (err && err.response && err.response.data && err.response.data.error) {
          errorMsg = err.response.data.error
        }
        message.error(errorMsg)
      })
  }

  getSubcontractorColumns = () => {
    return [
      {
        title: 'Name',
        dataIndex: 'name',
        key: 'name',
      },
      {
        title: 'Email',
        dataIndex: 'email',
        key: 'email',
        render: function (email) {
          if (!email) return <Tag color="red">No email</Tag>
          return email
        },
      },
      {
        title: 'Account',
        dataIndex: 'has_user',
        key: 'has_user',
        width: 100,
        render: function (hasUser) {
          return hasUser
            ? <Tag color="green">Active</Tag>
            : <Tag color="orange">No account</Tag>
        },
      },
    ]
  }

  render() {
    var { visible, onClose } = this.props
    var {
      projects,
      loadingProjects,
      selectedProjectId,
      subcontractors,
      ramsRecords,
      projectTitle,
      selectedSubcontractorIds,
      selectedRamsRecordId,
      loadingSubcontractors,
      submitting,
    } = this.state

    var hasRamsRecord = ramsRecords.length > 0
    var canSubmit = selectedProjectId &&
      selectedSubcontractorIds.length > 0 &&
      hasRamsRecord &&
      !submitting

    return (
      <Modal
        title="New RAMS Run"
        visible={visible}
        onCancel={onClose}
        width={800}
        footer={[
          <Button key="cancel" onClick={onClose}>
            Cancel
          </Button>,
          <Button
            key="send"
            type="primary"
            icon={<SendOutlined />}
            onClick={this.handleSubmit}
            loading={submitting}
            disabled={!canSubmit}
          >
            {submitting ? 'Sending...' : 'Send RAMS'}
          </Button>,
        ]}
      >
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', marginBottom: 4, fontWeight: 'bold' }}>
            Project
          </label>
          <Select
            placeholder="Select a project"
            style={{ width: '100%' }}
            showSearch
            optionFilterProp="children"
            value={selectedProjectId}
            onChange={this.handleProjectChange}
            loading={loadingProjects}
            notFoundContent={loadingProjects ? <Spin size="small" /> : 'No projects found'}
          >
            {(projects || []).map(function (project) {
              return (
                <Option key={project.id} value={project.id}>
                  {project.title}
                </Option>
              )
            })}
          </Select>
        </div>

        {loadingSubcontractors && (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <Spin tip="Loading project data..." />
          </div>
        )}

        {selectedProjectId && !loadingSubcontractors && (
          <div>
            {!hasRamsRecord && (
              <Alert
                type="warning"
                message="No RAMS record found for this project. Please create a RAMS record from the project view first."
                style={{ marginBottom: 16 }}
                showIcon
              />
            )}

            {ramsRecords.length > 1 && (
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 4, fontWeight: 'bold' }}>
                  RAMS Record
                </label>
                <Select
                  style={{ width: '100%' }}
                  value={selectedRamsRecordId}
                  onChange={this.handleRamsRecordChange}
                >
                  {ramsRecords.map(function (record) {
                    var label = 'RAMS #' + record.id
                    if (record.valid_from && record.valid_to) {
                      label += ' (' + record.valid_from + ' to ' + record.valid_to + ')'
                    }
                    if (record.scope) {
                      label += ' - ' + record.scope.substring(0, 50)
                    }
                    return (
                      <Option key={record.id} value={record.id}>
                        {label}
                      </Option>
                    )
                  })}
                </Select>
              </div>
            )}

            {ramsRecords.length === 1 && (
              <div style={{ marginBottom: 16 }}>
                <Tag color="blue">
                  RAMS #{ramsRecords[0].id}
                  {ramsRecords[0].valid_from && ramsRecords[0].valid_to
                    ? ' (' + ramsRecords[0].valid_from + ' to ' + ramsRecords[0].valid_to + ')'
                    : ''}
                </Tag>
              </div>
            )}

            <div style={{ marginBottom: 8 }}>
              <label style={{ fontWeight: 'bold' }}>
                Subcontractors
              </label>
              <span style={{ marginLeft: 8, color: '#888' }}>
                {selectedSubcontractorIds.length} of {subcontractors.length} selected
              </span>
            </div>

            {subcontractors.length === 0 ? (
              <Alert
                type="info"
                message="No subcontractors found booked on this project."
                showIcon
              />
            ) : (
              <Table
                rowKey="id"
                columns={this.getSubcontractorColumns()}
                dataSource={subcontractors}
                size="small"
                pagination={false}
                scroll={{ y: 300 }}
                rowSelection={{
                  selectedRowKeys: selectedSubcontractorIds,
                  onChange: this.handleSubcontractorSelectionChange,
                }}
              />
            )}
          </div>
        )}
      </Modal>
    )
  }
}
